import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi,type GridReadyEvent, type RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, Input, Tooltip } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import PopupCommon from "@components/popup/popup_common";
import { usePopup, type PopupAction } from "@components/popup/popup_context";
import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./popup_search_pn.scoped.scss";

import RegistPnPopup from "@user/views/popup/popup_regist_pn";

interface State {
  gridApi: GridApi | null,
  isShowRegistPn: boolean
}

const defState: State = {
  gridApi: null,
  isShowRegistPn: false
};

interface SearchParams {
  mngType: string,
  pnLike: string,
  remarkLike: string
}

const defSearchParams: SearchParams = {
  mngType: "",
  pnLike: "",
  remarkLike: ""
};

interface Result {
  searchParams: SearchParams,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  infos: []
};

function PopupSearchPn(props: any) {
  const { setOption, setActions, onClose } = usePopup();
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth("/master/pn_manage_list")));
  const gridStateKey = "/popup/popup_search_pn";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  // 팝업 설정
  const addPnRef = useRef(addPn);
  addPnRef.current = addPn;
  const selectPnRef = useRef(selectPn);
  selectPnRef.current = selectPn;
  useLayoutEffect(() => {
    setOption({
      title: "PN 검색",
      maxWidth: "1000px",
    });
    setActions([
      ...(props.data.mngType == "B" && auth.write ? [{
        id: "addPn",
        label: "신규등록",
        onClick: () => addPnRef.current(),
        position: "left",
        variant: "outlined",
      } satisfies PopupAction] : []),
      {
        id: "closePopup",
        label: "취소",
        onClick: () => onClose(),
        variant: "outlined",
      },
      {
        id: "selectPn",
        label: "확인",
        onClick: () => selectPnRef.current(),
        variant: "contained",
      }
    ]);
  }, [props.data.mngType, auth.write]);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Remark", headerClass: "hd-ta-left", field: "remark", width: 270, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "관리구분", field: "mngType", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("mngType"), filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "singleRow" as const,
      checkboxes: false,
      enableClickSelection: true
    },
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    },
    onRowDoubleClicked: (_e: RowDoubleClickedEvent) => {
      selectPn();
    }
  });

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await doSearch();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    searchParams.mngType = props.data.mngType;
    updateSearchParams(searchParams);
  }

  async function doSearch() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/popup/popup_search_pn.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    onClose();
  }

  async function addPn() {
    openPopupRegistPn();
  }

  async function selectPn() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("PN을 선택해 주세요.");
      return;
    }

    onClose({isSuccess: true, pn: selectedInfos[0].pn});
  }

  async function openPopupRegistPn() {
    setState(prev => ({ ...prev, isShowRegistPn: true }));
  }

  async function closePopupRegistPn(data: any) {
    setState(prev => ({ ...prev, isShowRegistPn: false }));

    if (data?.isSuccess) {
      onClose({isSuccess: true, pn: data.pn});
    }
  }

  return (
    <>
      {/* 팝업 내용 */}
      <div className="popup-contents">
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>P/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.pnLike}
              onChange={(e) => {updateSearchParams({ pnLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="P/N"
            />
          </div>
          <div>
            <p>Remark</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.remarkLike}
              onChange={(e) => {updateSearchParams({ remarkLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="Remark"
            />
          </div>
          <p>
            <Tooltip title="검색">
              <button className="icon-button popup-search" onClick={doSearch}>검색</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onRowDoubleClicked={gridCallbacks.onRowDoubleClicked}
            />
          </div>
        </AgGridProvider>
        {state.isShowRegistPn &&
          <PopupCommon onClose={closePopupRegistPn}>
            <RegistPnPopup data={{mngType: props.data.mngType}} />
          </PopupCommon>
        }
      </div>
      {/*// 팝업 내용 */}
    </>
  );
}

export default React.memo(PopupSearchPn);
